import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Filter,
  RefreshCw,
  Download,
  Clock,
  CheckCircle2,
  BookOpen,
  BarChart3,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { isLyrraOne } from '@/lib/platform';
import { InstitutionStyledSearchInput } from '@/components/institution/InstitutionStyledSearchInput';
import { InstitutionStyledSelect } from '@/components/institution/InstitutionStyledSelect';
import type { LearnerEntry } from './learnerTypesAndUtils';
import {
  formatDate,
  formatDuration,
  getEnrollmentProgressPercent,
  getInitials,
  ProgressBar,
} from './learnerTypesAndUtils';

interface FlowOption {
  id: string;
  title: string;
}

// ─── Components ─────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: string | number; color: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[#E8EDF5] bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', color)}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#0C1C38] dark:text-white">{value}</p>
        <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{label}</p>
      </div>
    </div>
  );
}

function LearnerRow({ learner, lang }: { learner: LearnerEntry; lang: string }) {
  const { t } = useTranslation();
  const p = learner.participant;
  const completedFlows = learner.enrollments.filter((e) => e.completed).length;
  const totalTime = learner.enrollments.reduce((sum, e) => sum + e.totalTimeSpent, 0);
  const avgProgress = learner.enrollments.length > 0
    ? Math.round(learner.enrollments.reduce((sum, e) => sum + getEnrollmentProgressPercent(e), 0) / learner.enrollments.length)
    : 0;
  const lastAccess = learner.enrollments
    .map((e) => e.lastAccessAt)
    .filter(Boolean)
    .sort()
    .reverse()[0] ?? null;
  const detailPath = isLyrraOne() ? `/dashboard/learners/${p.id}` : `/learners/${p.id}`;

  return (
    <div className="flex items-center gap-3 border-b border-[#F0F4FA] px-4 py-3 last:border-0 dark:border-gray-700/50 sm:gap-4">
      {/* Avatar */}
      {p.facePhotoUrl ? (
        <img src={p.facePhotoUrl} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
      ) : (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
          {getInitials(p.firstName, p.lastName, p.email)}
        </div>
      )}

      {/* Name & email */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-[#0C1C38] dark:text-white">
          {p.firstName || p.lastName ? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() : p.email}
        </p>
        {(p.firstName || p.lastName) && (
          <p className="truncate text-xs text-[#5B6472] dark:text-[#B6CAEC]">{p.email}</p>
        )}
      </div>

      <div className="hidden items-center gap-1 text-xs text-[#5B6472] sm:flex dark:text-[#B6CAEC]">
        <BookOpen className="h-3.5 w-3.5" />
        <span>{learner.enrollments.length}</span>
      </div>

      <div className="hidden w-24 sm:block">
        <ProgressBar percent={avgProgress} />
      </div>

      <div className="hidden items-center gap-1 text-xs sm:flex">
        <CheckCircle2 className={cn('h-3.5 w-3.5', completedFlows > 0 ? 'text-green-500' : 'text-[#5B6472]/50')} />
        <span className={completedFlows > 0 ? 'font-medium text-green-600' : 'text-[#5B6472]/70'}>
          {completedFlows}/{learner.enrollments.length}
        </span>
      </div>

      <div className="hidden items-center gap-1 text-xs text-[#5B6472] md:flex dark:text-[#B6CAEC]">
        <Clock className="h-3.5 w-3.5" />
        <span>{formatDuration(totalTime)}</span>
      </div>

      <div className="hidden text-xs text-[#5B6472]/70 lg:block">
        {lastAccess ? formatDate(lastAccess, lang) : '—'}
      </div>

      <Link
        to={detailPath}
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#E8EDF5] bg-white text-[#5B6472] transition hover:border-[color:var(--inst-player-primary,#0C1C38)] hover:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,transparent)] hover:text-[color:var(--inst-player-primary-deep,#19335E)] dark:border-gray-600 dark:bg-gray-800 dark:text-[#DCE7F7] dark:hover:border-teal-600"
        title={t('pages.learners.openProgressDashboard', 'Tableau de bord d’avancement')}
        aria-label={t('pages.learners.openProgressDashboard', 'Tableau de bord d’avancement')}
      >
        <BarChart3 className="h-4 w-4" aria-hidden />
      </Link>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function LearnersPage() {
  const { t, i18n } = useTranslation();
  usePageTitle(t('nav.learners', 'Learners'));

  const [learners, setLearners] = useState<LearnerEntry[]>([]);
  const [flows, setFlows] = useState<FlowOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterFlowId, setFilterFlowId] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<'recent' | 'name' | 'progress'>('recent');

  const flowFilterOptions = useMemo(
    () => [
      { value: '', label: t('pages.learners.filterAllFlows'), icon: Filter },
      ...flows.map((f) => ({ value: f.id, label: f.title, icon: BookOpen })),
    ],
    [flows, t],
  );

  const sortSelectOptions = useMemo(
    () => [
      { value: 'recent', label: t('pages.learners.sortRecent'), icon: Clock },
      { value: 'name', label: t('pages.learners.sortName'), icon: Users },
      { value: 'progress', label: t('pages.learners.sortProgress'), icon: BarChart3 },
    ],
    [t],
  );

  const fetchLearners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { page: String(page), limit: '20' };
      if (search.trim()) params.search = search.trim();
      if (filterFlowId) params.flowId = filterFlowId;

      const res: any = await api.get('/eduflows/learners', { params });
      // successResponse wraps in { success, data: { learners, pagination, flows } }
      const payload = res?.data ?? res;
      const list: LearnerEntry[] = payload?.learners ?? [];
      const pag = payload?.pagination ?? {};

      setLearners(list);
      setTotal(pag.total ?? list.length);
      setTotalPages(pag.totalPages ?? 1);

      if (payload?.flows) {
        setFlows((prev) => (prev.length === 0 ? payload.flows : prev));
      }
    } catch (err: any) {
      console.error('[LearnersPage] fetch error', err);
      setError(err?.message || t('pages.learners.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterFlowId, t]);

  useEffect(() => {
    fetchLearners();
  }, [fetchLearners]);

  // Sort locally
  const sorted = [...learners].sort((a, b) => {
    if (sortBy === 'name') {
      const na = `${a.participant.firstName ?? ''} ${a.participant.lastName ?? ''}`.trim() || a.participant.email;
      const nb = `${b.participant.firstName ?? ''} ${b.participant.lastName ?? ''}`.trim() || b.participant.email;
      return na.localeCompare(nb, i18n.language?.split('-')[0] ?? 'fr', { sensitivity: 'base' });
    }
    if (sortBy === 'progress') {
      const pa = a.enrollments.length > 0 ? a.enrollments.reduce((s, e) => s + getEnrollmentProgressPercent(e), 0) / a.enrollments.length : 0;
      const pb = b.enrollments.length > 0 ? b.enrollments.reduce((s, e) => s + getEnrollmentProgressPercent(e), 0) / b.enrollments.length : 0;
      return pb - pa;
    }
    return 0; // default: server order (recent)
  });

  // Stats
  const totalLearners = total;
  const activeLearners = learners.filter((l) => l.enrollments.some((e) => !e.completed && e.startedAt)).length;
  const completedLearners = learners.filter((l) => l.enrollments.every((e) => e.completed) && l.enrollments.length > 0).length;
  const avgCompletion = learners.length > 0
    ? Math.round(learners.reduce((sum, l) => {
        const avg = l.enrollments.length > 0
          ? l.enrollments.reduce((s, e) => s + getEnrollmentProgressPercent(e), 0) / l.enrollments.length
          : 0;
        return sum + avg;
      }, 0) / learners.length)
    : 0;

  const handleExportCSV = () => {
    if (learners.length === 0) return;
    const rows = [[
      t('pages.learners.csvLastName'),
      t('pages.learners.csvFirstName'),
      t('pages.learners.csvEmail'),
      t('pages.learners.csvFormations'),
      t('pages.learners.csvAvgProgress'),
      t('pages.learners.csvTotalTime'),
      t('pages.learners.csvLastActivity'),
    ]];
    for (const l of learners) {
      const avg = l.enrollments.length > 0
        ? Math.round(l.enrollments.reduce((s, e) => s + getEnrollmentProgressPercent(e), 0) / l.enrollments.length)
        : 0;
      const totalTime = l.enrollments.reduce((s, e) => s + e.totalTimeSpent, 0);
      const lastAccess = l.enrollments.map((e) => e.lastAccessAt).filter(Boolean).sort().reverse()[0] ?? '';
      rows.push([
        l.participant.lastName ?? '',
        l.participant.firstName ?? '',
        l.participant.email,
        String(l.enrollments.length),
        `${avg}%`,
        formatDuration(totalTime),
        lastAccess ? formatDate(lastAccess, i18n.language) : '',
      ]);
    }
    const csv = rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `apprenants_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-tour="tour-learners-root">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_12%,transparent)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_18%,transparent)]">
            <Users className="h-5 w-5 text-[color:var(--inst-player-primary,#0C1C38)]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0C1C38] dark:text-white">
              {t('pages.learners.title')}
            </h1>
            <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">
              {t('pages.learners.description')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchLearners}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-xs font-medium text-[#121826] transition-colors hover:bg-[#F8FAFC] dark:border-gray-600 dark:bg-gray-800 dark:text-[#DCE7F7] dark:hover:bg-gray-700"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            {t('pages.learners.refresh')}
          </button>
          <button
            onClick={handleExportCSV}
            disabled={learners.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-xs font-medium text-[#121826] transition-colors hover:bg-[#F8FAFC] disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-[#DCE7F7] dark:hover:bg-gray-700"
          >
            <Download className="h-3.5 w-3.5" />
            {t('pages.learners.exportCsv')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={Users} label={t('pages.learners.statTotal')} value={totalLearners} color="bg-[color:var(--inst-player-primary,#0C1C38)]" />
        <StatCard icon={BookOpen} label={t('pages.learners.statInProgress')} value={activeLearners} color="bg-blue-500" />
        <StatCard icon={CheckCircle2} label={t('pages.learners.statCompleted')} value={completedLearners} color="bg-green-500" />
        <StatCard icon={BarChart3} label={t('pages.learners.statAvgProgress')} value={`${avgCompletion}%`} color="bg-amber-500" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="min-w-0 flex-1" style={{ minWidth: 200 }}>
          <InstitutionStyledSearchInput
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder={t('pages.learners.searchPlaceholder')}
          />
        </div>

        {/* Flow filter */}
        {flows.length > 0 && (
          <div className="w-full min-w-0 shrink-0 sm:w-72">
            <InstitutionStyledSelect
              label={false}
              options={flowFilterOptions}
              value={filterFlowId}
              onChange={(v) => {
                setFilterFlowId(v);
                setPage(1);
              }}
            />
          </div>
        )}

        {/* Sort */}
        <div className="w-full min-w-0 shrink-0 sm:w-56">
          <InstitutionStyledSelect
            label={false}
            options={sortSelectOptions}
            value={sortBy}
            onChange={(v) => setSortBy(v as 'recent' | 'name' | 'progress')}
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#E8EDF5] bg-white dark:border-gray-700 dark:bg-gray-800">
        {/* Table header */}
        <div className="hidden items-center gap-3 border-b border-[#E8EDF5] bg-[#F8FAFC] px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-[#5B6472] sm:flex sm:gap-4 dark:border-gray-700 dark:bg-gray-800/80 dark:text-[#B6CAEC]">
          <div className="w-9 shrink-0" /> {/* avatar */}
          <div className="min-w-0 flex-1">{t('pages.learners.colLearner')}</div>
          <div className="flex shrink-0 items-center gap-1">
            <BookOpen className="h-3 w-3" /> {t('pages.learners.colFormations')}
          </div>
          <div className="w-24 shrink-0">{t('pages.learners.colProgress')}</div>
          <div className="flex shrink-0 items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> {t('pages.learners.colCompleted')}
          </div>
          <div className="hidden shrink-0 items-center gap-1 md:flex">
            <Clock className="h-3 w-3" /> {t('pages.learners.colTime')}
          </div>
          <div className="hidden w-24 shrink-0 lg:block">{t('pages.learners.colLastAccess')}</div>
          <div className="w-10 shrink-0 text-end" title={t('pages.learners.colActions', 'Actions')}>
            <BarChart3 className="ms-auto inline h-3.5 w-3.5 text-[#5B6472]/70" aria-hidden />
          </div>
        </div>

        {/* Loading */}
        {loading && learners.length === 0 && (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-[#5B6472]/70">
            <Loader2 className="h-5 w-5 animate-spin" />
            {t('pages.learners.loading')}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-red-500">
            <AlertCircle className="h-5 w-5" />
            {error}
            <button onClick={fetchLearners} className="ms-2 font-medium text-primary-500 hover:underline">
              {t('pages.learners.retry')}
            </button>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && learners.length === 0 && (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
              <Users className="h-7 w-7 text-[#5B6472]/70" />
            </div>
            <p className="text-sm font-medium text-[#0C1C38] dark:text-white">
              {search || filterFlowId ? t('pages.learners.emptyFiltered') : t('pages.learners.emptyNone')}
            </p>
            <p className="mt-1 text-xs text-[#5B6472] dark:text-[#B6CAEC]">
              {search || filterFlowId
                ? t('pages.learners.emptyFilteredHint')
                : t('pages.learners.emptyNoneHint')}
            </p>
          </div>
        )}

        {/* Rows */}
        {!loading && sorted.map((learner) => (
          <LearnerRow
            key={learner.participant.id}
            learner={learner}
            lang={i18n.language}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">
            {t('pages.learners.pagination', { page, totalPages, total })}
          </p>
          <div className="flex gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-lg border border-[#E8EDF5] bg-white px-3 py-1.5 text-xs font-medium text-[#121826] transition-colors hover:bg-[#F8FAFC] disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-[#DCE7F7]"
            >
              {t('pages.learners.prev')}
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-lg border border-[#E8EDF5] bg-white px-3 py-1.5 text-xs font-medium text-[#121826] transition-colors hover:bg-[#F8FAFC] disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-[#DCE7F7]"
            >
              {t('pages.learners.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LearnersPage;
